<template>
  <d-modal
    :active="isActive"
    :width="700"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-xl font-semibold text-secondary-500">
          {{ subjectArea.name }}
        </h3>
        <h2 class="text-4xl font-bold">
          Access Rules
        </h2>
      </div>

      <!-- Add Rule Form -->
      <div class="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Add New Rule</h3>
        <form @submit.prevent="saveRule()">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <d-field 
              label="Rule Identifier" 
              message="Full email address or email domain"
            >
              <d-input 
                v-model="form.identifier" 
                required 
                :type="form.type === 'Email' ? 'email' : 'text'"
                placeholder="example@domain.com or @domain.com"
              />
            </d-field>
            <d-field label="Rule Type">
              <d-select v-model="form.type" expanded required>
                <option value="Domain">
                  Email Domain
                </option>
                <option value="Email">
                  Full Email Address
                </option>
              </d-select>
            </d-field>
          </div>
          <div class="flex justify-end gap-3">
            <d-btn 
              size="sm" 
              flat 
              @click="launchImportRulesModal"
            >
              Import List of Rules
            </d-btn>
            <d-btn 
              type="submit" 
              size="sm" 
              :loading="saving"
              primary
            >
              Add Rule
            </d-btn>
          </div>
        </form>
      </div>

      <!-- Access Rules Lists -->
      <div class="mb-6">
        <div v-if="loading" class="flex justify-center py-10">
          <span class="loading loading-spinner loading-md"></span>
        </div>
        
        <div v-else class="space-y-6">
          <!-- Email Rules -->
          <div v-if="accessEmailRules.length > 0">
            <h3 class="text-lg font-bold mb-3 text-primary-600">
              Access By Email Address
            </h3>
            <div class="space-y-2">
              <div 
                v-for="rule in accessEmailRules" 
                :key="rule.id" 
                class="flex items-center justify-between p-3 bg-white border rounded-lg"
              >
                <div class="flex items-center gap-2">
                  <i class="fas fa-envelope text-primary-500"></i>
                  <span>{{ rule.identifier }}</span>
                </div>
                <d-btn 
                  size="xs" 
                  error 
                  flat 
                  @click="removeRule(rule, 'Email')"
                >
                  Remove
                </d-btn>
              </div>
            </div>
          </div>

          <!-- Domain Rules -->
          <div v-if="accessDomainRules.length > 0">
            <h3 class="text-lg font-bold mb-3 text-secondary-600">
              Access By Email Domain
            </h3>
            <div class="space-y-2">
              <div 
                v-for="rule in accessDomainRules" 
                :key="rule.id" 
                class="flex items-center justify-between p-3 bg-white border rounded-lg"
              >
                <div class="flex items-center gap-2">
                  <i class="fas fa-globe text-secondary-500"></i>
                  <span>{{ rule.identifier }}</span>
                </div>
                <d-btn 
                  size="xs" 
                  error 
                  flat 
                  @click="removeRule(rule, 'Domain')"
                >
                  Remove
                </d-btn>
              </div>
            </div>
          </div>

          <!-- No Rules Message -->
          <div v-if="accessEmailRules.length === 0 && accessDomainRules.length === 0" class="text-center py-8 text-gray-500">
            No access rules configured for this subject area.
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="flex justify-end gap-3 pt-4 border-t">
        <d-btn 
          flat 
          @click="handleClose"
        >
          Close
        </d-btn>
      </div>
    </div>

    <!-- Import Rules Modal -->
    <ImportSubjectAreaRulesModal
      v-if="showImportModal"
      :active="showImportModal"
      :subject-area="subjectArea"
      @close="closeImportModal"
      @success="handleImportSuccess"
    />
  </d-modal>
</template>

<script>
  import axios from 'axios';
  import { toast } from '@services/toast.service';
  import ImportSubjectAreaRulesModal from './ImportSubjectAreaRulesModal.vue';

  export default {
    name: 'SubjectAreaAccessModal',

    components: {
      ImportSubjectAreaRulesModal,
    },

    props: {
      active: {
        type: Boolean,
        default: false,
      },
      subjectArea: {
        type: Object,
        default: () => ({}),
      },
    },

    emits: ['close'],

    data() {
      return {
        form: {
          identifier: '',
          type: 'Domain',
        },
        defaultForm: {
          identifier: '',
          type: 'Domain',
        },
        saving: false,
        loading: false,
        accessEmailRules: [],
        accessDomainRules: [],
        showImportModal: false,
      };
    },

    computed: {
      isActive() {
        return this.active;
      },
    },

    watch: {
      active: {
        handler(newVal) {
          if (newVal) {
            this.getAccessRules();
          }
        },
        immediate: true,
      },
    },

    methods: {
      async getAccessRules() {
        if (!this.subjectArea?.id) return;

        this.loading = true;
        try {
          const { data } = await axios.get(`/api/subject-areas/${this.subjectArea.id}/access-rules`);
          this.accessEmailRules = data.filter((m) => m.type === 'Email');
          this.accessDomainRules = data.filter((m) => m.type === 'Domain');
        } catch (error) {
          console.error('Error loading access rules:', error);
          toast.error('Failed to load access rules');
          this.accessEmailRules = [];
          this.accessDomainRules = [];
        } finally {
          this.loading = false;
        }
      },

      async saveRule() {
        this.saving = true;
        try {
          const { data } = await axios.post(`/api/subject-areas/${this.subjectArea.id}/access-rules`, this.form);
          if (data.resultObject.type === 'Domain') {
            this.accessDomainRules.push(data.resultObject);
          } else if (data.resultObject.type === 'Email') {
            this.accessEmailRules.push(data.resultObject);
          }
          this.form = JSON.parse(JSON.stringify(this.defaultForm));
          toast.success('Access rule added successfully');
        } catch (error) {
          console.error('Error saving rule:', error);
          toast.error('Failed to save access rule');
        } finally {
          this.saving = false;
        }
      },

      launchImportRulesModal() {
        this.showImportModal = true;
      },

      closeImportModal() {
        this.showImportModal = false;
      },

      handleImportSuccess() {
        this.closeImportModal();
        this.getAccessRules();
        toast.success('Rules imported successfully');
      },

      async removeRule(rule, type) {
        try {
          await axios.delete(`/api/subject-areas/access-rules/${rule.id}`);
          if (type === 'Email') {
            this.accessEmailRules = this.accessEmailRules.filter((m) => m.id !== rule.id);
          } else if (type === 'Domain') {
            this.accessDomainRules = this.accessDomainRules.filter((m) => m.id !== rule.id);
          }
          toast.success('Access rule removed successfully');
        } catch (error) {
          console.error('Error removing rule:', error);
          toast.error('Failed to remove access rule');
        }
      },

      handleClose() {
        this.$emit('close');
      },
    },
  };
</script>
